import $ from 'jquery';
import _ from 'underscore';
import {
  $body,
  $window,
  jqGetElemByCmpName,
  jqGetElemById,
  isMobile,
  jqOffOn,
  setScrollPosition,
} from 'util/global';
import autosize from 'lib/autosize';
import { crop, showConfirmationModal, showGenericModal } from 'util/ui';
import {
  CLASS_NAME_HIDE,
  EVT_QUIZ_POSITION_WILL_CHANGE,
  EVT_VKEY_AFTER_UPDATE,
} from 'util/constants';
import * as deferred from 'util/deferred';
import { showCommentsForSolution } from 'app/solution_discussions';
import 'lib/bootstrap';

const SIDEBAR_STATE_FREE_TRIAL = 0;
const SIDEBAR_STATE_ADD_QUESTION = 1;
const SIDEBAR_STATE_SUCCESS = 2;
const SIDEBAR_STATE_QUESTION_LIST = 3;
const SIDEBAR_FLEXIBLE_AREA_FORM = 10;
const SIDEBAR_FLEXIBLE_AREA_QUESTIONS = 11;

let didHandleDirectLink = false;

/**
 * Load all of the deferred comments components
 * @param {JQuery} $container
 */
function showCommentsInContainer($container) {
  $container.find('.discsol-post-wrapper').each((i, elem) => {
    showCommentsForSolution($(elem), true);
  });
}

/**
 * The header size can be variable, so move the top of the flexible stuff to accommodate
 * @param {number} flexArea
 */
function resizeFlexibleArea(prefix, flexArea) {
  const $sidebar = jqGetElemById(`${prefix}-wrapper`);
  const $header = $sidebar.find('.qanda-add-form header:visible');
  const $flexArea =
    flexArea === SIDEBAR_FLEXIBLE_AREA_FORM
      ? $sidebar.find(`#${prefix}-add-form textarea`)
      : $sidebar.find(`#${prefix}-list .discsol-list`);
  let newTop = '';

  if ($header.length) {
    newTop = $header.outerHeight();
  }

  $flexArea.css({ top: newTop });
}

/**
 * Show the add question form
 * @param {JQuery} $sidebar
 */
function setSidebarState($sidebar, state) {
  const prefix = $sidebar.data('prefix');
  const addQuestionClass = 'add-question';
  const addQuestionSuccessClass = 'add-question-success';
  const freeTrialClass = 'free-trial';

  switch (state) {
    case SIDEBAR_STATE_FREE_TRIAL:
      $sidebar
        .removeClass(`${addQuestionClass} ${addQuestionSuccessClass}`)
        .addClass(freeTrialClass);
      break;
    case SIDEBAR_STATE_ADD_QUESTION:
      $sidebar.removeClass(addQuestionSuccessClass).addClass(addQuestionClass);
      $('#qanda-add-form textarea').select();
      resizeFlexibleArea(prefix, SIDEBAR_FLEXIBLE_AREA_FORM);
      break;
    case SIDEBAR_STATE_SUCCESS:
      $sidebar.removeClass(addQuestionClass).addClass(addQuestionSuccessClass);
      resizeFlexibleArea(prefix, SIDEBAR_FLEXIBLE_AREA_QUESTIONS);
      break;
    case SIDEBAR_STATE_QUESTION_LIST:
      $sidebar.removeClass(`${addQuestionClass} ${addQuestionSuccessClass} ${freeTrialClass}`);
      resizeFlexibleArea(prefix, SIDEBAR_FLEXIBLE_AREA_QUESTIONS);
      break;
    default:
      throw new Error(`Unexpected Q&A sidebar state: ${state}`);
  }
}

function handleDirectLinks($container, showSidebar) {
  if (didHandleDirectLink) {
    // don't handle a link more than once
    return;
  }

  const { commentsSlug, commentsCmpKey } = $container.data();
  const regex = new RegExp(`^#!\\/${commentsSlug}\\/(\\d+)\\/?(\\d+)?\\/?$`);

  // example hash: #!/qanda-question/123/456
  // example hash: #!/course-feedback/123/456
  // example hash: #!/content-reports/123/456
  // example hash: #!/dailyproblem-feedback/123/456
  const matches = window.location.hash.match(regex);
  if (matches) {
    const questionId = matches[1];
    const commentId = matches[2];
    const $questionList = $container.find('.discsol-list');
    let $targetQuestion = null;
    let $targetComment = null;

    const adjustScrollPosition = () => {
      const $targetElement = $targetQuestion || $targetComment;
      if ($targetElement) {
        const questionsOffset = $questionList.offset().top;
        const commentOffset = $targetElement.offset().top;
        const questionsHeight = $questionList.height();
        const commentHeight = $targetElement.height();

        if (commentOffset + commentHeight - questionsOffset - questionsHeight > 0) {
          setScrollPosition(commentOffset - questionsOffset - 50, {
            $container: $questionList,
          });
        }
      }
    };

    const applyHighlight = $element => {
      const highlightClass = 'highlight';
      if ($element.data(highlightClass) !== true) {
        $element.addClass(highlightClass);
        setTimeout(() => $element.removeClass(highlightClass), 3000);
        $element.data(highlightClass, true);
      }
    };

    if (questionId && !commentId) {
      const $question = jqGetElemById(`post-${questionId}`);
      if ($question) {
        $targetQuestion = $question;
        setTimeout(() => applyHighlight($question), 500);
        adjustScrollPosition();
      }
    }

    $window.on(`${EVT_VKEY_AFTER_UPDATE}.${commentsCmpKey}`, e => {
      const { $cmp } = e;

      if (commentId) {
        const $comment = $cmp.find(`[data-comment=${commentId}]`);

        if ($comment.length) {
          $targetComment = $comment;

          if (!$comment.is(':visible')) {
            $cmp.find('.cmmnt-container.level-0').addClass('show-all-replies');
          }

          applyHighlight($comment);
        }
      }

      adjustScrollPosition();
    });

    setTimeout(showSidebar);
    didHandleDirectLink = true;
  }
}

/**
 * Q&A sidebar events
 * @param {JQuery} $container
 */
export function sidebar($container) {
  const prefix = $container.data('prefix');
  const sidebarActiveClass = 'qanda-active';
  const targetId = $container.data('target');
  const showFreeTrial = $container.data('show-free-trial');

  // hide if can't view questions, or if non-pro and no questions
  const isCourseFeedback = prefix === 'course-feedback';
  const isContentReports = prefix === 'content-reports';
  const containerHideQuestions = $container.data('hide-questions');
  const noQuestionsComponents =
    $container.find('.discsol-post').length === 0 &&
    jqGetElemByCmpName('qanda_questionadd').length === 0;
  const hideQuestions =
    !isCourseFeedback && !isContentReports && (containerHideQuestions || noQuestionsComponents);

  const hideSidebar = (forceHide = false) => {
    if (forceHide === true) {
      $container.removeClass('in');
      $container.addClass('hide');
    } else {
      $container.one('transitionend', () => $container.addClass('hide'));
      $container.removeClass('in');
    }
    $body.removeClass(sidebarActiveClass);
    $body.off(EVT_QUIZ_POSITION_WILL_CHANGE);
    crop(false);
  };

  const showSidebar = () => {
    $container.removeClass('hide');
    $body.addClass(sidebarActiveClass);
    crop();

    const $addFormInput = $container.find(`#${prefix}-add-form textarea`);
    if (showFreeTrial) {
      setSidebarState($container, SIDEBAR_STATE_FREE_TRIAL);
    } else if (hideQuestions || ($addFormInput.length && $addFormInput.val().length > 0)) {
      setSidebarState($container, SIDEBAR_STATE_ADD_QUESTION);
    } else if (isCourseFeedback || isContentReports) {
      setSidebarState($container, SIDEBAR_STATE_QUESTION_LIST);
    }

    setTimeout(() => {
      $container.addClass('in');
      $container.one('click', hideSidebar);

      const $deferredContentReportsCmp = $container.find(
        '.cmp-deferred[data-cmp_component_key="contentreports_postlist"]',
      );
      if (isContentReports && $deferredContentReportsCmp.length) {
        const extraGETParams = {};
        if ($('.solv-details').hasClass('endstate')) {
          // user just solved the problem and since the component is loaded independently
          // here, correctly filtered content reports list won't be cached at this point,
          // so explicitly passing this flag to the component
          extraGETParams.solver_viewing = true;
        }
        deferred.load($deferredContentReportsCmp, extraGETParams);
      } else {
        showCommentsInContainer($container);
      }

      $body.on(EVT_QUIZ_POSITION_WILL_CHANGE, () => hideSidebar(true));
    });
  };

  $container.find('.close').not('.modal .close').on('click', hideSidebar);
  $container.find(`#${prefix}-non-pro-premium-trigger-${targetId}`).on('click', e => {
    e.preventDefault();
    $container.removeClass('free-trial');
    if (hideQuestions) {
      setSidebarState($container, SIDEBAR_STATE_ADD_QUESTION);
    } else {
      setSidebarState($container, SIDEBAR_STATE_QUESTION_LIST);
    }
  });

  jqOffOn($body, `click.${prefix}`, `#${prefix}-trigger-${targetId}`, showSidebar);

  $container.children().on('click', e => {
    e.stopPropagation();

    // stopPropagation messes with dropdowns so close dropdowns when clicking something
    $container.find('.dropdown, .dropup').removeClass('open');
  });

  // stopPropagation messes with dropdowns so apply dropdown events manually
  $container.find('[data-toggle=dropdown]').dropdown();

  if (!hideQuestions) {
    handleDirectLinks($container, showSidebar);
  }
}

/**
 * Add question component controller
 * @param {JQuery} $container
 */
export function addPost($container) {
  const { prefix, success } = $container.data();
  const $wrapper = jqGetElemById(`${prefix}-wrapper`);
  const $addForm = jqGetElemById(`${prefix}-add-form`);
  const $addFormInput = $addForm.find('textarea');
  const $addFormButton = $addForm.find('button');
  let previousInputLength = 0;

  const checkInputLength = () => {
    if ($addFormInput.length === 0) {
      return;
    }

    const currentInputLength = $addFormInput.val().length;
    if (currentInputLength > 0 && previousInputLength === 0) {
      $addForm.prop('disabled', false);
      $addFormButton.prop('disabled', false);
    } else if (currentInputLength === 0 && previousInputLength >= 0) {
      $addForm.prop('disabled', true);
      $addFormButton.prop('disabled', true);
    }

    previousInputLength = currentInputLength;
  };

  $addFormInput.on('input', checkInputLength);
  if (isMobile) {
    $addFormInput.on('focus', () => $body.scrollTop(0));
  }

  $addForm.on('submit', () => {
    setTimeout(() => $addForm.trigger($.Event('autosave.clear')));
  });

  jqGetElemById(`${prefix}-back`).on('click', () => {
    if (prefix === 'qanda' && $wrapper.data('show-free-trial')) {
      setSidebarState($wrapper, SIDEBAR_STATE_FREE_TRIAL);
    } else {
      setSidebarState($wrapper, SIDEBAR_STATE_QUESTION_LIST);
    }
  });

  if (success === true) {
    setSidebarState($wrapper, SIDEBAR_STATE_SUCCESS);
  } else {
    resizeFlexibleArea(prefix, SIDEBAR_FLEXIBLE_AREA_FORM);
    $addFormInput.select();
  }

  checkInputLength();
}

/**
 * Question list component controller
 * @param {JQuery} $container
 */
export function postsList($container) {
  const prefix = $container.data('prefix');

  if ($container.is(':visible')) {
    showCommentsInContainer($container);
  }

  $container.find('[data-toggle=dropdown]').dropdown();

  jqGetElemById(`${prefix}-add-post-trigger`).on('click', event => {
    const proPaywallUrl = $(event.currentTarget).data('paywall');
    if (proPaywallUrl) {
      window.location.href = proPaywallUrl;
    } else {
      setSidebarState(jqGetElemById(`${prefix}-wrapper`), SIDEBAR_STATE_ADD_QUESTION);
    }
  });

  resizeFlexibleArea(prefix, SIDEBAR_FLEXIBLE_AREA_QUESTIONS);

  $container.find('.staff-details-link').on('click', e => {
    const $trigger = $(e.currentTarget);
    const $staffDetails = $trigger.closest('.discsol-post').find('.staff-details');
    const EVENT_NAME = 'click.contentReportsStaffDetails';

    e.preventDefault();
    $staffDetails.toggleClass(CLASS_NAME_HIDE);
    if (!$staffDetails.hasClass(CLASS_NAME_HIDE)) {
      _.defer(() => {
        $('.qanda-sidebar').on(EVENT_NAME, ev => {
          if ($(ev.target).closest('.staff-details').length === 0) {
            $trigger.trigger('click');
          }
        });
      });
    } else {
      $('.qanda-sidebar').off(EVENT_NAME);
    }
  });

  $container.find('.canned-response-template-trigger').on('click', e => {
    e.preventDefault();
    const $target = $(e.currentTarget);
    const $addCommentTrigger = $target
      .parents('.qanda-question')
      .find('.qanda-add-comment-trigger');
    const $commentContainer = jqGetElemById($addCommentTrigger.data('target'));
    const $firstReplyTextarea = $commentContainer
      .find('.top-level-comment-form')
      .find('textarea#id_text_');

    const showCommentsList = () => {
      $addCommentTrigger.parent().addClass('hide');
      $commentContainer.removeClass('hide');
    };

    const insertCannedResponse = response => {
      $firstReplyTextarea.val(response).trigger($.Event('input'));
      $target.closest('.discsol-post').find('.staff-details-link').trigger('click');
    };

    if (!$addCommentTrigger.parent().hasClass('hide')) {
      showCommentsList();
    }

    autosize($firstReplyTextarea);

    if ($firstReplyTextarea.length === 0) {
      showGenericModal('You can only used canned response on the first reply.');
    } else if ($firstReplyTextarea.val()) {
      showConfirmationModal({
        title: 'Overwrite reply?',
        text: 'You have a current reply drafted already, are you sure you want to overwrite it?',
        cancelBtnText: 'No',
        confirmBtnText: 'Yes, overwrite',
      }).then(confirmed => {
        if (confirmed) {
          insertCannedResponse($target.data('text'));
        }
      });
    } else {
      insertCannedResponse($target.data('text'));
    }
  });
}

/**
 * Comment list component controller
 * @param {JQuery} $container
 */
export function commentsList($container) {
  const $addCommentTrigger = $container.find('.qanda-add-comment-trigger');
  const showCommentsList = () => {
    $addCommentTrigger.parent().addClass('hide');
    jqGetElemById($addCommentTrigger.data('target')).removeClass('hide');
  };

  $addCommentTrigger.on('click', showCommentsList);

  $container.find('[data-toggle=dropdown]').dropdown();

  if ($container.find('.errorlist').length) {
    showCommentsList();
  }
}

/**
 * Changes visibility of a report after confirmation.
 * @param {JQuery} $trigger
 */
export function changeReportVisibility($trigger) {
  const data = $trigger.data();
  const $modal = $trigger.closest('.modal');

  $trigger.on('click', e => {
    e.preventDefault();
    $modal.modal('hide'); // hide the current modal (if any) before showing a new one
    showConfirmationModal({
      title: data.confirmModalTitle,
    }).then(confirmed => {
      if (confirmed) {
        const $report = jqGetElemById(`post-${data.reportId}`);
        const $form = $report.find(`.make-visible-to-${data.visibilityType}`);
        $form.trigger('submit');
      } else {
        $modal.modal('show'); // restore the previous modal (if any)
      }
    });
  });
}
